import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
    CONSENT_CHANGED_EVENT,
    acceptAll,
    effectiveConsent,
    hasDecided,
    rejectAll,
    writeConsent,
} from "@/lib/cookieConsent";
import { track } from "@/lib/track";

const Toggle = ({ checked, onChange, disabled, label, description, testId }) => (
    <label
        htmlFor={testId}
        className={`flex items-start justify-between gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${
            disabled
                ? "bg-gray-50 border-gray-200 cursor-not-allowed"
                : checked
                  ? "bg-brand-red/5 border-brand-red/30"
                  : "bg-white border-gray-200 hover:border-gray-300"
        }`}
    >
        <div className="flex-1 min-w-0">
            <p className="font-semibold text-brand-dark text-sm">{label}</p>
            <p className="text-xs text-brand-dark/65 mt-1 leading-relaxed">
                {description}
            </p>
        </div>
        <button
            type="button"
            role="switch"
            id={testId}
            data-testid={testId}
            aria-checked={checked}
            disabled={disabled}
            onClick={() => !disabled && onChange(!checked)}
            className={`relative shrink-0 inline-flex h-6 w-11 rounded-full transition-colors outline-none focus:ring-2 focus:ring-brand-red/40 ${
                disabled
                    ? "bg-brand-red/60 cursor-not-allowed"
                    : checked
                      ? "bg-brand-red"
                      : "bg-gray-300"
            }`}
        >
            <span
                className={`inline-block h-5 w-5 bg-white rounded-full shadow transform transition-transform mt-0.5 ${
                    checked ? "translate-x-5" : "translate-x-0.5"
                }`}
            />
        </button>
    </label>
);

const CookieBanner = () => {
    const [show, setShow] = useState(false);
    const [customizeOpen, setCustomizeOpen] = useState(false);
    const [draft, setDraft] = useState(() => effectiveConsent());

    // Reveal banner on mount if user has not decided.
    useEffect(() => {
        if (!hasDecided()) {
            // Slight delay so we don’t fight CLS on first paint.
            const t = setTimeout(() => setShow(true), 600);
            return () => clearTimeout(t);
        }
    }, []);

    // Expose a global handler so the footer "Gérer les cookies" link can
    // reopen the customize modal without prop drilling.
    useEffect(() => {
        const openHandler = () => {
            setDraft(effectiveConsent());
            setCustomizeOpen(true);
            setShow(true);
        };
        window.addEventListener("capitalauto-open-cookie-prefs", openHandler);
        return () =>
            window.removeEventListener(
                "capitalauto-open-cookie-prefs",
                openHandler,
            );
    }, []);

    const closeAll = () => {
        setShow(false);
        setCustomizeOpen(false);
        // Let tracking layer pick up the new state.
        window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
    };

    const onAcceptAll = () => {
        acceptAll();
        track("cookie_consent_accept_all", {});
        closeAll();
    };

    const onRejectAll = () => {
        rejectAll();
        track("cookie_consent_reject_all", {});
        closeAll();
    };

    const onSaveCustom = () => {
        writeConsent({
            analytics: !!draft.analytics,
            marketing: !!draft.marketing,
        });
        track("cookie_consent_custom_save", {
            analytics: !!draft.analytics,
            marketing: !!draft.marketing,
        });
        closeAll();
    };

    return (
        <AnimatePresence>
            {show ? (
                <>
                    {/* Banner — only if not in customize modal */}
                    {!customizeOpen ? (
                        <motion.div
                            key="banner"
                            initial={{ y: 100, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 100, opacity: 0 }}
                            transition={{ duration: 0.35, ease: "easeOut" }}
                            role="dialog"
                            aria-labelledby="cookie-banner-title"
                            data-testid="cookie-banner"
                            className="fixed bottom-0 left-0 right-0 z-[60] bg-brand-dark text-white shadow-2xl"
                        >
                            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                    <Cookie className="w-6 h-6 text-brand-red shrink-0 mt-0.5" />
                                    <p
                                        id="cookie-banner-title"
                                        className="text-sm leading-relaxed"
                                    >
                                        Nous utilisons des cookies pour mesurer l’audience
                                        (Google Analytics) et améliorer nos campagnes
                                        publicitaires (Google Ads). Vos données restent
                                        confidentielles. Vous pouvez accepter, refuser ou
                                        personnaliser.{" "}
                                        <Link
                                            to="/politique-confidentialite"
                                            onClick={() => setShow(false)}
                                            data-testid="cookie-banner-policy-link"
                                            className="underline text-white hover:text-brand-red transition-colors"
                                        >
                                            En savoir plus →
                                        </Link>
                                    </p>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2 shrink-0 w-full md:w-auto">
                                    <button
                                        type="button"
                                        data-testid="cookie-banner-reject"
                                        onClick={onRejectAll}
                                        className="px-4 py-2.5 rounded-full border-2 border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
                                    >
                                        Refuser
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="cookie-banner-customize"
                                        onClick={() => {
                                            setDraft(effectiveConsent());
                                            setCustomizeOpen(true);
                                        }}
                                        className="px-4 py-2.5 rounded-full border-2 border-white/30 text-white text-sm font-semibold hover:bg-white/10 transition-colors"
                                    >
                                        Personnaliser
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="cookie-banner-accept-all"
                                        onClick={onAcceptAll}
                                        className="px-5 py-2.5 rounded-full bg-brand-red text-white text-sm font-bold hover:bg-brand-red-hover transition-colors red-cta-shadow"
                                    >
                                        Accepter tout
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ) : null}

                    {/* Customize modal */}
                    {customizeOpen ? (
                        <motion.div
                            key="modal-backdrop"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="fixed inset-0 z-[65] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
                            onClick={() => setCustomizeOpen(false)}
                            data-testid="cookie-customize-backdrop"
                        >
                            <motion.div
                                key="modal"
                                initial={{ y: 30, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: 30, opacity: 0 }}
                                transition={{ duration: 0.3, ease: "easeOut" }}
                                role="dialog"
                                aria-labelledby="cookie-customize-title"
                                data-testid="cookie-customize-modal"
                                onClick={(e) => e.stopPropagation()}
                                className="w-full max-w-lg bg-white rounded-t-3xl md:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col"
                            >
                                <div className="flex items-center justify-between p-5 md:p-6 border-b border-gray-100">
                                    <h2
                                        id="cookie-customize-title"
                                        className="text-xl font-extrabold text-brand-dark"
                                    >
                                        Personnaliser les cookies
                                    </h2>
                                    <button
                                        type="button"
                                        data-testid="cookie-customize-close"
                                        onClick={() => setCustomizeOpen(false)}
                                        className="w-10 h-10 rounded-full flex items-center justify-center text-brand-dark/60 hover:bg-gray-100 transition-colors"
                                        aria-label="Fermer"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                                <div className="p-5 md:p-6 space-y-3 overflow-y-auto">
                                    <Toggle
                                        checked={true}
                                        disabled
                                        onChange={() => {}}
                                        label="Cookies essentiels"
                                        description="Nécessaires au fonctionnement du site (navigation, formulaire, sécurité). Toujours actifs."
                                        testId="cookie-toggle-essential"
                                    />
                                    <Toggle
                                        checked={!!draft.analytics}
                                        onChange={(v) =>
                                            setDraft((d) => ({ ...d, analytics: v }))
                                        }
                                        label="Analytics (Google Analytics 4)"
                                        description="Mesure d’audience anonymisée pour comprendre comment vous utilisez le site. Aucune donnée personnelle collectée."
                                        testId="cookie-toggle-analytics"
                                    />
                                    <Toggle
                                        checked={!!draft.marketing}
                                        onChange={(v) =>
                                            setDraft((d) => ({ ...d, marketing: v }))
                                        }
                                        label="Marketing (Google Ads)"
                                        description="Suivi des conversions publicitaires pour optimiser nos campagnes. Aucun retargeting sur d’autres sites."
                                        testId="cookie-toggle-marketing"
                                    />
                                    <p className="text-xs text-brand-dark/55 pt-2">
                                        Voir notre{" "}
                                        <Link
                                            to="/politique-confidentialite"
                                            onClick={() => closeAll()}
                                            className="text-brand-red font-semibold underline"
                                        >
                                            politique de confidentialité
                                        </Link>{" "}
                                        pour plus de détails.
                                    </p>
                                </div>
                                <div className="p-5 md:p-6 border-t border-gray-100 flex flex-col-reverse sm:flex-row gap-3 justify-end">
                                    <button
                                        type="button"
                                        data-testid="cookie-customize-cancel"
                                        onClick={() => setCustomizeOpen(false)}
                                        className="px-5 py-2.5 rounded-full border-2 border-gray-300 text-brand-dark font-semibold text-sm hover:border-brand-dark transition-colors"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        type="button"
                                        data-testid="cookie-customize-save"
                                        onClick={onSaveCustom}
                                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-brand-red text-white font-bold text-sm hover:bg-brand-red-hover transition-colors red-cta-shadow"
                                    >
                                        <Check className="w-4 h-4" />
                                        Enregistrer mon choix
                                    </button>
                                </div>
                            </motion.div>
                        </motion.div>
                    ) : null}
                </>
            ) : null}
        </AnimatePresence>
    );
};

export default CookieBanner;
